import { Component, OnInit, OnDestroy } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import moment from 'moment';
import { AuthService } from 'src/app/core/services/auth.service';
import { CompanyService } from 'src/app/core/services/company.service';
import { PlanService } from 'src/app/core/services/plan.service';
import { AlertAndNotificationsService } from 'src/app/core/services/alert-and-notifications.service';

// Native port of the legacy changeCompanyState/changeCompanyStateModal pair
// (src/app/public/app/partials/missioncontrol/company/changeCompanyState,
// changeCompanyStateModal). changeCompanyStateModal
// (change-company-state-modal.component.js) was the $uibModal wrapper opened
// by company-page.component.js's launchCompanyStateChangeModal
// (component: 'changeCompanyStateModal'), and it embedded changeCompanyState
// (change-company-state.component.js, <change-company-state> element) as its
// non-Delete body content - confirmed by change-company-state-modal.html:20-27
// nesting <change-company-state>, and by changeCompanyState not being opened
// directly as a modal/state anywhere else in the app. A native BsModalService
// modal doesn't need that wrapper/content-component split, so both are merged
// into this one component, same pattern as CompanyPageModifyPlanModalComponent.
//
// The Delete branch reuses the already-native <app-delete-entity
// entity="company"> component unchanged, same as the legacy modal's own
// Delete branch did with <delete-entity entity="company">.
@Component({
  selector: 'app-company-page-change-company-state-modal',
  templateUrl: './company-page-change-company-state-modal.component.html',
  styleUrls: ['./company-page-change-company-state-modal.component.css']
})
export class CompanyPageChangeCompanyStateModalComponent implements OnInit, OnDestroy {

  public onClose: Subject<any>;

  modalData: any = {};

  companyInfo: any;
  changeToState: string;

  isLoading = true;
  changingStateFlag = false;
  isTrialPlanActive = false;
  stateText = '';
  date: string;
  time: string;
  assessmentsLeft: number;
  jobBoardsPullLeft: number;

  companyChangeStateObject: any = {};

  private clockInterval: any;

  callbackObject: any = { callbackType: 'cancel', entity: {} };

  constructor(
    private bsModalRef: BsModalRef,
    public bsModalService: BsModalService,
    private authService: AuthService,
    private companyService: CompanyService,
    private planService: PlanService,
    private alertsAndNotificationsService: AlertAndNotificationsService
  ) {
    this.onClose = new Subject();
    if (this.bsModalService.config.initialState) {
      this.modalData = this.bsModalService.config.initialState;
    }
  }

  ngOnInit() {
    this.companyInfo = this.modalData.companyInfo;
    this.changeToState = this.modalData.changeToState;

    if (this.changeToState === 'Delete') {
      this.isLoading = false;
      return;
    }

    this.companyChangeStateObject = {
      userId: this.authService.getUserDetails().id,
      companyId: this.companyInfo.companyId,
      companyState: this.changeToState,
      techAssessmentLimitForSoftHold: 5
    };

    this._getCompanyPlanDetails();
    this._setDateAndTime();
    this._setStateText();
    this._setNote();
  }

  ngOnDestroy() {
    if (this.clockInterval) {
      clearInterval(this.clockInterval);
    }
  }

  _getCompanyPlanDetails() {
    this.planService.getCompanyPlanDetails(this.companyInfo.companyId, (data: any) => {
      if (data && data.plan) {
        this.isTrialPlanActive = data.plan === 'Trial';
        if (this.changeToState === 'SoftHold') {
          if (this.isTrialPlanActive) {
            this._getAvailableAssessments();
            this._getAvailableJobBoardsPull();
          } else {
            this._setAssessmentLimitDefault();
            this.isLoading = false;
          }
        } else {
          this.isLoading = false;
        }
      } else {
        this.isLoading = false;
      }
    }, (error: any) => {
      this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
    });
  }

  _getAvailableAssessments() {
    const jobId = null;
    this.planService.getAvailableTechAssessmentsCount(this.companyInfo.companyId, jobId, (data: any) => {
      this.assessmentsLeft = data['techAssessmentsLeft'];
    }, (error: any) => {
      this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
    });
  }

  _getAvailableJobBoardsPull() {
    this.planService.getAvailableJobBoardsPullCount(this.companyInfo.companyId, (data: any) => {
      this.jobBoardsPullLeft = data['jobBoardResumesLeftCount'];
      this.isLoading = false;
    }, (error: any) => {
      this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
    });
  }

  _setAssessmentLimitDefault() {
    this.planService.getCurrentPlan(this.companyInfo.companyId, (data: any) => {
      if (data.planHeaderDto.planType === 'Month to Month') {
        this.companyChangeStateObject.techAssessmentLimitForSoftHold = 10;
      } else {
        this.companyChangeStateObject.techAssessmentLimitForSoftHold = 25;
      }
    }, (error: any) => {
      this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
    });
  }

  _setDateAndTime() {
    this.date = moment().format('ll');
    this.time = moment().format('hh:mm:ss a');
    this.clockInterval = setInterval(() => {
      this.date = moment().format('ll');
      this.time = moment().format('hh:mm:ss a');
    }, 1000);
  }

  _setStateText() {
    if (this.changeToState === 'Active') {
      this.stateText = 'Release';
    } else if (this.changeToState === 'SoftHold') {
      this.stateText = 'Soft Hold';
    } else {
      this.stateText = this.changeToState;
    }
  }

  _setNote() {
    this.companyChangeStateObject.note = this.stateText + ' Company';
  }

  changeCompanyState() {
    this.changingStateFlag = true;
    this.companyService.changeCompanyState(this.companyChangeStateObject, (data: any) => {
      this.changingStateFlag = false;
      this.alertsAndNotificationsService.showBannerMessage("Company '" + this.companyInfo.companyName + "' state changed to " + this.stateText + ' successfully', 'success');
      this.callbackObject = { callbackType: 'stateChange', entity: {} };
      this.onClose.next(this.callbackObject);
      this.bsModalRef.hide();
    }, (error: any) => {
      this.changingStateFlag = false;
      this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
    });
  }

  onCompanyDelete() {
    this.callbackObject = { callbackType: 'delete', entity: {} };
    this.closeModal();
  }

  receiveFormalizedComment(event: string) {
    this.companyChangeStateObject.note = event;
  }

  onVoiceTranscript(transcript: string) {
    if (this.companyChangeStateObject.note) {
      this.companyChangeStateObject.note += ' ' + transcript;
    } else {
      this.companyChangeStateObject.note = transcript;
    }
  }

  closeModal() {
    this.onClose.next(this.callbackObject);
    this.bsModalRef.hide();
  }

  cancel() {
    this.callbackObject = { callbackType: 'cancel', entity: {} };
    this.closeModal();
  }
}
